import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Spinner } from "./Spinner";

type Variant = "primary" | "secondary" | "ghost" | "subtle" | "danger";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-pressed shadow-card border border-transparent",
  secondary:
    "bg-card text-ink border border-border hover:border-border-emphasized hover:bg-hover active:bg-pressed",
  ghost: "text-ink-secondary hover:text-ink hover:bg-hover active:bg-pressed border border-transparent",
  subtle: "bg-accent-subtle text-accent border border-transparent hover:bg-selected",
  // Destructive, in the house idiom: `bg-error-subtle text-error` is what
  // every other refusal and failure in this app already looks like, and the
  // palette has no `on-error` to put text on a filled red. The border is what
  // separates "this button deletes something" from a passive error notice.
  danger: "bg-error-subtle text-error border border-error/40 hover:border-error active:bg-error-subtle",
};

const SIZES: Record<Size, string> = {
  sm: "control-sm px-3 type-support",
  md: "control-md px-4 type-label",
  lg: "control-lg px-5 type-label",
};

export function Button({
  variant = "secondary",
  size = "md",
  loading = false,
  icon,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled ?? loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-element whitespace-nowrap",
        "transition-[background-color,border-color,color,box-shadow] motion-fast",
        "disabled:pointer-events-none disabled:opacity-55",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  children: ReactNode;
}

export function IconButton({ label, className, children, ...props }: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex control-md aspect-square items-center justify-center rounded-element",
        "text-ink-secondary hover:text-ink hover:bg-hover active:bg-pressed",
        "transition-[background-color,color] motion-fast",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
